import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Building2, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { PawktLogo } from "@/components/pawkt/PawktLogo";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const [mode, setMode] = useState(planParam ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", plan: planParam || undefined });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [err, setErr] = useState(() => { const m = sessionStorage.getItem("pawkt_login_error") || ""; sessionStorage.removeItem("pawkt_login_error"); return m; });
  const [showPw, setShowPw] = useState(false);

  const sendForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setForgotMsg(data.message);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => { setErr(""); setForm({ ...form, [k]: e.target.value }); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      navigate(u?.signup_payment_required ? "/checkout" : "/dashboard");
    } catch (err) {
      const st = err?.response?.status;
      setErr(!err?.response ? "We couldn't reach PAWKT. Check your connection and try again."
        : st === 429 ? apiErr(err) || "Too many attempts. Please wait a few minutes and try again." : apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // Carry the selected pricing plan across the OAuth round-trip so a paid Google signup is
    // gated to payment exactly like email signup. Same-origin storage survives the redirect.
    if (planParam) sessionStorage.setItem("pawkt_signup_plan", planParam);
    else sessionStorage.removeItem("pawkt_signup_plan");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #9E86FD 0, transparent 40%), radial-gradient(circle at 80% 60%, #6A48F0 0, transparent 40%)" }} />
        <div className="relative">
          <PawktLogo variant="auth" wordmarkClassName="text-white" />
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight text-white">
            Run your pet business.<br /><span className="text-emerald-400">All in one place.</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
            The complete operating system for pet-care — customers, pets, bookings, POS, payments and insights, unified.
          </p>
          <div className="mt-8 flex gap-6 text-slate-400 text-sm">
            <div><div className="font-display text-2xl font-bold text-white">360°</div>Pet & parent profiles</div>
            <div><div className="font-display text-2xl font-bold text-white">₹</div>Built for India</div>
            <div><div className="font-display text-2xl font-bold text-white">Multi</div>Tenant & branch</div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 PAWKT · Pet Business Operating System</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <PawktLogo variant="mobile" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            {mode === "login" ? "Welcome back" : "Create your business"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {mode === "login" ? "Sign in to your PAWKT workspace" : "Start running your pet business in minutes"}
          </p>

          <button type="button" onClick={google} data-testid="google-login-btn"
            className="mt-6 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-pawkt-border bg-white py-3 text-sm font-semibold text-pawkt-text hover:bg-pawkt-elevated transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> OR SIGN IN WITH EMAIL <div className="h-px flex-1 bg-slate-200" />
          </div>

          {forgot ? (
            <form onSubmit={sendForgot} className="space-y-3" data-testid="forgot-form">
              <p className="text-sm font-semibold text-slate-800">Forgot your password?</p>
              <p className="text-xs text-slate-500">Enter your login email. Your business owner will be asked to set a temporary password for you.</p>
              {forgotMsg ? (
                <p data-testid="forgot-confirmation" className="rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">{forgotMsg}</p>
              ) : (
                <>
                  <Field icon={Mail} type="email" placeholder="Login email" value={form.email} onChange={set("email")} testid="forgot-email" required />
                  <button type="submit" disabled={busy} data-testid="forgot-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-white disabled:opacity-60">
                    {busy ? "Please wait…" : "Request password reset"}
                  </button>
                </>
              )}
              <button type="button" onClick={() => { setForgot(false); setForgotMsg(""); }} data-testid="forgot-back" className="w-full text-sm font-semibold text-emerald-600 hover:underline">Back to sign in</button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Field icon={User} label="Your name" placeholder="Your name" autoComplete="name" value={form.name} onChange={set("name")} testid="reg-name" />
                <Field icon={Building2} label="Business name" placeholder="Business name" autoComplete="organization" value={form.business_name} onChange={set("business_name")} testid="reg-business" />
                {planParam === "free" && (
                  <p data-testid="free-plan-hint" className="text-xs font-medium text-emerald-600">Starting on the Free plan — ₹0/month, no credit card required.</p>
                )}
                {planParam && planParam !== "free" && (
                  <p data-testid="paid-plan-hint" className="text-xs font-medium text-slate-600">Selected plan: <span className="capitalize font-semibold">{planParam.replace("_", "-")}</span>. You'll complete secure payment next to activate it.</p>
                )}
              </>
            )}
            <Field icon={Mail} type="email" label="Email" name="email" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}
              placeholder="you@business.com" value={form.email} onChange={set("email")} testid="login-email" required />
            <Field icon={Lock} type={showPw ? "text" : "password"} label="Password" name="password" autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder="Your password" value={form.password} onChange={set("password")} testid="login-password" required
              trailing={<button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} aria-pressed={showPw}
                className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-slate-500 hover:bg-slate-100" data-testid="login-password-toggle">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>} />
            {err && <p role="alert" data-testid="login-error" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">{err}</p>}
            {mode === "login" && (
              <div className="text-right">
                <button type="button" onClick={() => setForgot(true)} data-testid="login-forgot-password-link" className="text-xs font-semibold text-emerald-600 hover:underline">Forgot Password?</button>
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover active:bg-pawkt-primary-deep transition-colors disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <>{mode === "login" ? "Sign in" : "Create business"} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          )}

          {!forgot && <p className="mt-5 text-center text-sm text-slate-500">
            {mode === "login" ? "New to PAWKT? " : "Already have an account? "}
            <button onClick={() => { setErr(""); setMode(mode === "login" ? "register" : "login"); }} data-testid="toggle-mode"
              className="font-semibold text-emerald-600 hover:underline">
              {mode === "login" ? "Create a business" : "Sign in"}
            </button>
          </p>}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, label, trailing, ...props }) {
  const input = (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input {...props} data-testid={testid} aria-label={label || props.placeholder}
        className={`w-full min-h-[48px] rounded-[14px] border border-pawkt-border bg-white pl-10 ${trailing ? "pr-12" : "pr-3"} py-3 text-base sm:text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15`} />
      {trailing}
    </div>
  );
  return label ? <label className="block"><span className="mb-1.5 block text-xs font-semibold text-slate-700">{label}</span>{input}</label> : input;
}
